import { computed, type Injector, type Signal } from '@angular/core';
import { assertInjector } from './assert-injector';
import {
    DeviceDetectorService,
    MAJOR_VIEWPORT_BREAKPOINTS,
    MINOR_VIEWPORT_BREAKPOINTS,
} from './device-detector.service';

type MajorBreakpoint = keyof typeof MAJOR_VIEWPORT_BREAKPOINTS;
type MinorBreakpoint = keyof typeof MINOR_VIEWPORT_BREAKPOINTS;
type BreakpointName<Custom extends Readonly<Record<string, number>>> =
    | MajorBreakpoint
    | MinorBreakpoint
    | (keyof Custom & string);

export interface ViewportBreakpoints<Name extends string> {
    /** Less than 640px. */
    readonly isPhone: Signal<boolean>;
    /** At least 640px and less than 1024px. */
    readonly isTablet: Signal<boolean>;
    /** At least 1024px and less than 1280px. */
    readonly isLaptop: Signal<boolean>;
    /** At least 1280px. */
    readonly isDesktop: Signal<boolean>;
    lessThan(name: Name): Signal<boolean>;
    atLeast(name: Name): Signal<boolean>;
}

export function injectViewportBreakpoints<
    const Custom extends Readonly<Record<string, number>> = {},
>(
    options?: { custom?: Custom },
    injector?: Injector
): ViewportBreakpoints<BreakpointName<Custom>> {
    const service = assertInjector(injectViewportBreakpoints, injector).get(
        DeviceDetectorService
    );
    const custom = options?.custom ?? {};

    for (const [name, width] of Object.entries(custom)) {
        if (
            name in MAJOR_VIEWPORT_BREAKPOINTS ||
            name in MINOR_VIEWPORT_BREAKPOINTS
        ) {
            throw new Error(`Custom viewport breakpoint '${name}' is reserved`);
        }
        if (typeof width !== 'number' || !Number.isFinite(width) || width < 0) {
            throw new Error(
                `Custom viewport breakpoint '${name}' must be a non-negative finite width`
            );
        }
    }

    const widths: Readonly<Record<string, number>> = {
        ...MAJOR_VIEWPORT_BREAKPOINTS,
        ...MINOR_VIEWPORT_BREAKPOINTS,
        ...custom,
    };

    function widthFor(name: string): number {
        if (!Object.hasOwn(widths, name)) {
            throw new Error(`Unknown viewport breakpoint '${name}'`);
        }
        return widths[name];
    }

    const atLeastTablet = service.atLeast(MAJOR_VIEWPORT_BREAKPOINTS.tablet);
    const atLeastLaptop = service.atLeast(MAJOR_VIEWPORT_BREAKPOINTS.laptop);
    const atLeastDesktop = service.atLeast(MINOR_VIEWPORT_BREAKPOINTS.desktop);

    return {
        isPhone: computed(() => !atLeastTablet()),
        isTablet: computed(() => atLeastTablet() && !atLeastLaptop()),
        isLaptop: computed(() => atLeastLaptop() && !atLeastDesktop()),
        isDesktop: computed(() => atLeastDesktop()),
        lessThan: (name) => service.lessThan(widthFor(name)),
        atLeast: (name) => service.atLeast(widthFor(name)),
    };
}
